/* ==========================================================================
   EKBATAN — motion.js
   Progressive motion layer. Loaded only when motion is enabled in Theme
   settings; does nothing when the visitor prefers reduced motion.

   One shared requestAnimationFrame loop drives every scroll-linked effect,
   and only for elements currently near the viewport.
   ========================================================================== */
(() => {
  'use strict';

  const root = document.documentElement;
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  if (reduce.matches || !root.classList.contains('motion')) return;
  window.__motionReady = true;

  const expressive = root.dataset.motion === 'expressive';
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  const lowPower =
    (navigator.connection && navigator.connection.saveData) ||
    (navigator.hardwareConcurrency && navigator.hardwareConcurrency <= 4) ||
    window.innerWidth < 750;
  const depthScale = (expressive ? 1 : 0.55) * (lowPower ? 0.6 : 1);

  const clamp = (v, min = 0, max = 1) => Math.min(max, Math.max(min, v));
  const easeOut = (t) => 1 - Math.pow(1 - t, 3);
  let vh = window.innerHeight;

  /* ---------- Reveal + ring drawing ---------- */
  const revealIO = new IntersectionObserver((entries) => {
    let stagger = 0;
    entries
      .filter((e) => e.isIntersecting)
      .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top || a.boundingClientRect.left - b.boundingClientRect.left)
      .forEach((entry) => {
        const el = entry.target;
        if (el.hasAttribute('data-rings')) {
          el.classList.add('is-drawn');
        } else {
          el.style.setProperty('--stagger', Math.min(stagger++, 6));
          el.classList.add('is-in');
        }
        revealIO.unobserve(el);
      });
  }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });

  /* ---------- Scroll-linked registry ---------- */
  const active = new Set();
  const nearIO = new IntersectionObserver((entries) => {
    entries.forEach((e) => {
      const fx = e.target.__fx;
      if (!fx) return;
      if (e.isIntersecting) active.add(fx); else active.delete(fx);
    });
    request();
  }, { rootMargin: '25% 0px 25% 0px' });

  const register = (el, update) => {
    if (el.__fx) return;
    el.__fx = { el, update };
    nearIO.observe(el);
  };

  let queued = false;
  const request = () => { if (!queued) { queued = true; requestAnimationFrame(frame); } };
  const frame = () => {
    queued = false;
    active.forEach((fx) => fx.update(fx.el.getBoundingClientRect()));
  };
  window.addEventListener('scroll', request, { passive: true });
  /* Mobile address bars change innerHeight while scrolling; recomputing scroll maths on
     those changes makes sections lurch. Only react to real resizes (width or rotation). */
  let lastW = window.innerWidth;
  window.addEventListener('resize', () => {
    const touchy = window.matchMedia('(hover: none), (pointer: coarse)').matches;
    if (touchy && window.innerWidth === lastW && Math.abs(window.innerHeight - vh) < 160) return;
    lastW = window.innerWidth;
    vh = window.innerHeight;
    request();
  }, { passive: true });

  /* Parallax: element drifts against the scroll, relative to viewport centre. */
  const parallax = (el, attr) => {
    const speed = parseFloat(el.getAttribute(attr)) || 0.15;
    const host = el.parentElement || el;
    register(host, (rect) => {
      const centre = rect.top + rect.height / 2 - vh / 2;
      const y = (-centre * speed * depthScale).toFixed(1);
      el.style.transform = `translate3d(0, ${y}px, 0)`;
    });
  };

  /* Banner window that widens to full bleed. */
  const widen = (el) => {
    register(el, (rect) => {
      const p = clamp((vh - rect.top) / (vh * 0.85));
      el.style.setProperty('--w', easeOut(p).toFixed(4));
    });
  };

  /* Manifesto: words illuminate as the reader moves down the page. */
  const splitWords = (container) => {
    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
    const nodes = [];
    while (walker.nextNode()) if (walker.currentNode.textContent.trim()) nodes.push(walker.currentNode);
    nodes.forEach((node) => {
      const frag = document.createDocumentFragment();
      node.textContent.split(/(\s+)/).forEach((part) => {
        if (!part) return;
        if (/^\s+$/.test(part)) { frag.appendChild(document.createTextNode(part)); return; }
        const span = document.createElement('span');
        span.className = 'w';
        span.textContent = part;
        frag.appendChild(span);
      });
      node.replaceWith(frag);
    });
    container.setAttribute('data-split', '');
    return [...container.querySelectorAll('.w')];
  };
  const wordReveal = (el) => {
    const words = splitWords(el);
    let lit = -1;
    register(el, (rect) => {
      const p = clamp((vh * 0.88 - rect.top) / (rect.height + vh * 0.32));
      const count = Math.round(p * words.length);
      if (count === lit) return;
      words.forEach((w, i) => w.classList.toggle('is-lit', i < count));
      lit = count;
    });
  };

  /* Counting figures (keeps the merchant's formatting, e.g. 2,560). */
  const counter = (el) => {
    const raw = el.dataset.count || el.textContent;
    const target = parseFloat(raw.replace(/[^0-9.]/g, ''));
    if (!isFinite(target) || target === 0) return;
    const decimals = (raw.split('.')[1] || '').replace(/\D/g, '').length;
    const grouped = /\d,\d{3}/.test(raw);
    const prefix = raw.match(/^[^0-9]*/)[0];
    const suffix = raw.match(/[^0-9.,]*$/)[0];
    const render = (n) => {
      let s = n.toFixed(decimals);
      if (grouped) s = Number(s).toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
      el.textContent = prefix + s + suffix;
    };
    render(0);
    const io = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
      io.disconnect();
      const start = performance.now();
      const dur = 1800 + Math.min(target, 3000) * 0.15;
      const tick = (now) => {
        const t = clamp((now - start) / dur);
        render(target * easeOut(t));
        if (t < 1) requestAnimationFrame(tick); else el.textContent = raw;
      };
      requestAnimationFrame(tick);
    }, { threshold: 0.6 });
    io.observe(el);
  };

  /* ---------- Opening: aperture expansion + pointer depth ---------- */
  class HeroOpening extends HTMLElement {
    connectedCallback() {
      const expand = this.classList.contains('opening--expand');
      if (expand) {
        register(this, (rect) => {
          const travel = rect.height - vh;
          const p = travel > 0 ? clamp(-rect.top / travel) : 0;
          /* The aperture opens with an ease-in-out over the first 85% of the travel,
             leaving a held beat at full bleed for the caption. */
          const t = clamp(p / 0.85);
          const e = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
          this.style.setProperty('--p', p.toFixed(4));
          this.style.setProperty('--e', e.toFixed(4));
        });
      }
      if (this.dataset.tilt === 'true' && finePointer && !lowPower) this.enableTilt();
    }
    enableTilt() {
      const stage = this.querySelector('.opening__stage');
      const target = { x: 0, y: 0 };
      const current = { x: 0, y: 0 };
      let running = false;
      const amp = expressive ? 1 : 0.5;
      const loop = () => {
        current.x += (target.x - current.x) * 0.06;
        current.y += (target.y - current.y) * 0.06;
        this.style.setProperty('--tx', `${(current.x * -14 * amp).toFixed(2)}px`);
        this.style.setProperty('--ty', `${(current.y * -10 * amp).toFixed(2)}px`);
        this.style.setProperty('--rx', `${(current.x * 22 * amp).toFixed(2)}px`);
        this.style.setProperty('--ry', `${(current.y * 16 * amp).toFixed(2)}px`);
        if (Math.abs(target.x - current.x) > 0.001 || Math.abs(target.y - current.y) > 0.001) requestAnimationFrame(loop);
        else running = false;
      };
      const kick = () => { if (!running) { running = true; requestAnimationFrame(loop); } };
      stage.addEventListener('pointermove', (e) => {
        const r = stage.getBoundingClientRect();
        target.x = ((e.clientX - r.left) / r.width - 0.5) * 2;
        target.y = ((e.clientY - r.top) / r.height - 0.5) * 2;
        kick();
      });
      stage.addEventListener('pointerleave', () => { target.x = 0; target.y = 0; kick(); });
    }
  }
  customElements.define('hero-opening', HeroOpening);

  /* ---------- Product chapters ---------- */
  class StoryChapters extends HTMLElement {
    connectedCallback() {
      this.chapters = [...this.querySelectorAll('.chapter')];
      this.visuals = [...this.querySelectorAll('[data-visual]')];
      this.counter = this.querySelector('[data-chapter-current]');
      const list = this.querySelector('.chapters__list');
      if (!this.chapters.length) return;

      const io = new IntersectionObserver((entries) => {
        entries.forEach((e) => { if (e.isIntersecting) this.activate(this.chapters.indexOf(e.target)); });
      }, { rootMargin: '-45% 0px -45% 0px' });
      this.chapters.forEach((c) => io.observe(c));
      this.activate(0);

      register(list, (rect) => {
        const p = clamp((vh * 0.5 - rect.top) / rect.height);
        this.style.setProperty('--chapter-progress', p.toFixed(4));
      });

      /* Theme Editor: selecting a chapter block scrolls it into focus. */
      document.addEventListener('shopify:block:select', (e) => {
        const i = this.chapters.indexOf(e.target);
        if (i > -1) { this.activate(i); e.target.scrollIntoView({ block: 'center' }); }
      });
    }
    activate(i) {
      if (i < 0 || i === this.current) return;
      this.current = i;
      this.chapters.forEach((c, n) => c.classList.toggle('is-active', n === i));
      this.visuals.forEach((v, n) => v.classList.toggle('is-active', n === i));
      if (this.counter) this.counter.textContent = this.chapters[i].dataset.numeral || String(i + 1);
    }
  }
  customElements.define('story-chapters', StoryChapters);

  /* ---------- Scroll film: the visitor's scroll drives the film ----------
     The decoder is only asked for a new frame once the previous seek has
     landed, and the playhead eases toward the scroll position, so scrubbing
     stays fluid instead of queueing seeks. */
  class ScrollFilm extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video');
      this.texts = [...this.querySelectorAll('.scrollfilm__text')].map((el) => ({
        el, start: parseFloat(el.dataset.start) || 0, end: parseFloat(el.dataset.end) || 1
      }));
      this.target = 0;
      this.current = 0;
      this.mq = window.matchMedia('(max-width: 749px)');

      /* Phones and tablets decode seeks slowly, so scrubbing stutters there.
         They play the video normally instead, with the text following playback. */
      this.playMode = this.dataset.touchMode !== 'scrub' && window.matchMedia('(hover: none), (pointer: coarse)').matches;
      if (this.playMode) { this.setupPlayMode(); return; }

      register(this, (rect) => {
        const travel = rect.height - vh;
        const p = travel > 0 ? clamp(-rect.top / travel) : 0;
        this.style.setProperty('--p', p.toFixed(4));
        this.texts.forEach((t) => t.el.classList.toggle('is-visible', p >= t.start && p <= t.end + (t.end >= 1 ? 0.01 : 0)));
        if (this.ready && this.duration) {
          this.target = p * (this.duration - 0.04);
          this.kick();
        }
      });

      this._seen = new IntersectionObserver(([entry]) => root.classList.toggle('is-scrub-active', entry.isIntersecting));
      this._seen.observe(this);

      if (!this.video) return;
      const near = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) { near.disconnect(); this.load(); }
      }, { rootMargin: '150% 0px 150% 0px' });
      near.observe(this);
      this.mq.addEventListener?.('change', () => { if (this._src) this.load(true); });
    }
    disconnectedCallback() {
      this._seen && this._seen.disconnect();
      this._playIO && this._playIO.disconnect();
      root.classList.remove('is-scrub-active');
    }
    setupPlayMode() {
      this.classList.add('scrollfilm--play');
      this.showTexts(0);
      if (!this.video) return;
      const v = this.video;
      v.loop = true;
      v.muted = true;
      v.playsInline = true;
      const src = () => (this.mq.matches ? this.dataset.srcTall : this.dataset.srcWide) || this.dataset.srcWide;
      /* Start downloading about two screens before the section, so it can play on arrival. */
      const prefetch = new IntersectionObserver(([entry]) => {
        if (!entry.isIntersecting || this._src) return;
        prefetch.disconnect();
        this._src = src();
        if (!this._src) return;
        v.preload = 'auto';
        v.src = this._src;
        v.load();
        v.addEventListener('playing', () => this.classList.add('is-ready'), { once: true });
        if (this.visible) v.play().catch(() => {});
      }, { rootMargin: '200% 0px 200% 0px' });
      prefetch.observe(this);
      this._playIO = new IntersectionObserver(([entry]) => {
        this.visible = entry.isIntersecting;
        if (entry.isIntersecting) {
          if (this._src) {
            const pr = v.play();
            if (pr && pr.catch) pr.catch(() => {});
          }
          this.followPlayback();
        } else if (!v.paused) {
          v.pause();
        }
      }, { threshold: 0.1 });
      this._playIO.observe(this);
      document.addEventListener('visibilitychange', () => { if (document.hidden) v.pause(); else if (this.visible) v.play().catch(() => {}); });
    }
    followPlayback() {
      if (this._following) return;
      this._following = true;
      const v = this.video;
      const step = () => {
        if (v.duration) {
          const p = v.currentTime / v.duration;
          this.style.setProperty('--p', p.toFixed(4));
          this.showTexts(p);
        }
        if (this.visible) {
          if (v.requestVideoFrameCallback && !v.paused) v.requestVideoFrameCallback(step);
          else requestAnimationFrame(step);
        } else {
          this._following = false;
        }
      };
      step();
    }
    showTexts(p) {
      this.texts.forEach((t) => t.el.classList.toggle('is-visible', p >= t.start && p <= t.end + (t.end >= 1 ? 0.01 : 0)));
    }
    load(force) {
      const src = this.mq.matches ? this.dataset.srcTall : this.dataset.srcWide;
      if (!src || (src === this._src && !force)) return;
      this._src = src;
      this.ready = false;
      this.classList.remove('is-ready');
      const v = this.video;
      v.muted = true;
      v.preload = 'auto';
      v.src = src;
      v.load();
      const onData = () => {
        v.removeEventListener('loadeddata', onData);
        this.duration = v.duration;
        /* Prime the decoder (needed by iOS before seeking paints frames). */
        const done = () => { this.ready = true; this.classList.add('is-ready'); request(); };
        const pr = v.play();
        if (pr && pr.then) pr.then(() => { v.pause(); done(); }).catch(done); else { v.pause(); done(); }
      };
      v.addEventListener('loadeddata', onData);
    }
    kick() {
      if (this._raf) return;
      const step = () => {
        this._raf = null;
        const v = this.video;
        this.current += (this.target - this.current) * (expressive ? 0.16 : 0.24);
        if (Math.abs(this.target - this.current) < 0.003) this.current = this.target;
        if (!v.seeking && Math.abs(v.currentTime - this.current) > 0.015) {
          if (typeof v.fastSeek === 'function' && Math.abs(v.currentTime - this.current) > 0.5) v.fastSeek(this.current);
          else v.currentTime = this.current;
        }
        if (this.current !== this.target || v.seeking) this._raf = requestAnimationFrame(step);
      };
      this._raf = requestAnimationFrame(step);
    }
  }
  customElements.define('scroll-film', ScrollFilm);

  /* ---------- Glass: light follows the cursor ----------
     One delegated listener; only the glass surface under the pointer updates. */
  if (finePointer && root.dataset.glassLight === 'true' && root.dataset.glass === 'on') {
    let lastGlass = null;
    let pending = null;
    let framed = false;
    document.addEventListener('pointermove', (e) => {
      pending = e;
      if (framed) return;
      framed = true;
      requestAnimationFrame(() => {
        framed = false;
        const ev = pending;
        const glass = ev.target.closest && ev.target.closest('.glass, .glass-lit, .showcase__frame');
        if (lastGlass && lastGlass !== glass) { lastGlass.style.removeProperty('--gx'); lastGlass.style.removeProperty('--gy'); }
        if (glass) {
          const r = glass.getBoundingClientRect();
          glass.style.setProperty('--gx', `${(((ev.clientX - r.left) / r.width) * 100).toFixed(1)}%`);
          glass.style.setProperty('--gy', `${(((ev.clientY - r.top) / r.height) * 100).toFixed(1)}%`);
        }
        lastGlass = glass;
      });
    }, { passive: true });
  }

  /* ---------- Boot (and re-boot for injected content) ---------- */
  const boot = (scope = document) => {
    scope.querySelectorAll('[data-reveal]:not(.is-in), [data-rings]:not(.is-drawn)').forEach((el) => revealIO.observe(el));
    scope.querySelectorAll('[data-parallax]').forEach((el) => parallax(el, 'data-parallax'));
    scope.querySelectorAll('[data-depth-scroll]').forEach((el) => parallax(el, 'data-depth-scroll'));
    scope.querySelectorAll('[data-widen]').forEach(widen);
    scope.querySelectorAll('[data-word-reveal]:not([data-split])').forEach(wordReveal);
    scope.querySelectorAll('[data-count]').forEach(counter);
    request();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot());
  else boot();

  document.addEventListener('theme:content', (e) => boot(e.detail || document));
  document.addEventListener('shopify:section:load', (e) => boot(e.target));

  /* If the visitor switches on reduced motion mid-visit, settle everything. */
  reduce.addEventListener?.('change', (e) => {
    if (!e.matches) return;
    root.classList.remove('motion');
    document.querySelectorAll('[data-parallax], [data-depth-scroll]').forEach((el) => { el.style.transform = ''; });
  });
})();
